import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { ChevronLeft, Clock, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	formatMeetingElapsedTime,
	formatMeetingScheduledStartTime,
	getMeetingElapsedMs,
	isMeetingNotYetStarted,
} from '@/components/session-call/formatMeetingElapsedTime';

const COLLAPSED_SIZE_PX = 44;
const EXPANDED_RADIUS_PX = 8;
const ICON_SIZE_PX = 18;
const COLLAPSED_CLOCK_INSET_PX = (COLLAPSED_SIZE_PX - ICON_SIZE_PX) / 2;
const COLLAPSED_CLOCK_OFFSET_PX = COLLAPSED_CLOCK_INSET_PX + ICON_SIZE_PX;
const COLLAPSED_CHEVRON_LEFT_PADDING_PX = 12;
const COLLAPSED_CHEVRON_REVEAL_PX = COLLAPSED_CHEVRON_LEFT_PADDING_PX + 16;
const COLLAPSED_HOVER_WIDTH_PX =
	COLLAPSED_CHEVRON_REVEAL_PX + 6 + COLLAPSED_CLOCK_INSET_PX + ICON_SIZE_PX;
const COLLAPSED_HOVER_MARGIN_PX = COLLAPSED_HOVER_WIDTH_PX - COLLAPSED_SIZE_PX;
const HORIZONTAL_PADDING_PX = 16;
const ICON_TEXT_GAP_PX = 8;
const MORPH_DURATION_MS = 280;
const TEXT_FADE_MS = 160;
const COLLAPSED_HOVER_DURATION_MS = 150;

const MORPH_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';
const motionTransition = `${MORPH_DURATION_MS}ms ${MORPH_EASING}`;
const shellTransition = `width ${motionTransition}, border-radius ${motionTransition}, margin-left ${motionTransition}`;
const collapsedHoverTransition = `width ${COLLAPSED_HOVER_DURATION_MS}ms ease-out, margin-left ${COLLAPSED_HOVER_DURATION_MS}ms ease-out, border-radius ${COLLAPSED_HOVER_DURATION_MS}ms ease-out`;
const collapsedChevronTransition = `width ${COLLAPSED_HOVER_DURATION_MS}ms ease-out, opacity ${COLLAPSED_HOVER_DURATION_MS}ms ease-out`;
const clockTransition = `left ${motionTransition}`;
const textTransition = `opacity ${TEXT_FADE_MS}ms ${MORPH_EASING}, transform ${TEXT_FADE_MS}ms ${MORPH_EASING}, max-width ${motionTransition}`;
const chevronTransition = `width ${motionTransition}, opacity ${TEXT_FADE_MS}ms ${MORPH_EASING}`;

type SessionCallMeetingTimerProps = {
	scheduledStartMs: number;
	className?: string;
};

const SessionCallMeetingTimer = ({
	scheduledStartMs,
	className,
}: SessionCallMeetingTimerProps) => {
	const [nowMs, setNowMs] = useState(() => Date.now());
	const [isMinimized, setIsMinimized] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [isMorphing, setIsMorphing] = useState(false);
	const [expandedWidth, setExpandedWidth] = useState(COLLAPSED_SIZE_PX);
	const measureRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		const intervalId = window.setInterval(() => {
			setNowMs(Date.now());
		}, 1000);

		return () => window.clearInterval(intervalId);
	}, []);

	const isWaiting = isMeetingNotYetStarted(scheduledStartMs, nowMs);
	const scheduledStartTime =
		formatMeetingScheduledStartTime(scheduledStartMs);
	const elapsedLabel = formatMeetingElapsedTime(
		getMeetingElapsedMs(scheduledStartMs, nowMs),
	);
	const waitingLabel = `Sessão inicia às ${scheduledStartTime}`;
	const timeLabel = isWaiting ? waitingLabel : elapsedLabel;
	const ariaLabel = isWaiting
		? waitingLabel
		: `Tempo de reunião: ${elapsedLabel}`;

	const textLeftPx = HORIZONTAL_PADDING_PX + ICON_SIZE_PX + ICON_TEXT_GAP_PX;

	const measureExpandedWidth = useCallback(() => {
		const width = measureRef.current?.getBoundingClientRect().width;
		if (width) {
			setExpandedWidth(Math.ceil(width));
		}
	}, []);

	useLayoutEffect(() => {
		if (isMinimized || isMorphing) return;
		measureExpandedWidth();
	}, [isMinimized, isMorphing, measureExpandedWidth, timeLabel, isWaiting]);

	useEffect(() => {
		if (isMinimized || isMorphing) return;

		const observer = new ResizeObserver(measureExpandedWidth);
		const node = measureRef.current;
		if (node) observer.observe(node);

		return () => observer.disconnect();
	}, [isMinimized, isMorphing, measureExpandedWidth]);

	useEffect(() => {
		const node = buttonRef.current;
		if (!node) return;

		const handleTransitionEnd = (event: TransitionEvent) => {
			if (event.target !== node || event.propertyName !== 'width') return;
			setIsMorphing(false);
		};

		node.addEventListener('transitionend', handleTransitionEnd);
		return () =>
			node.removeEventListener('transitionend', handleTransitionEnd);
	}, []);

	const handleToggle = () => {
		measureExpandedWidth();
		setIsHovered(false);
		setIsMorphing(true);

		requestAnimationFrame(() => {
			setIsMinimized((current) => !current);
		});
	};

	const collapsedHoverActive = isMinimized && isHovered && !isMorphing;
	const shellWidth = isMinimized ? COLLAPSED_SIZE_PX : expandedWidth;
	const shellMarginLeft = collapsedHoverActive
		? -COLLAPSED_HOVER_MARGIN_PX
		: 0;
	const shellWidthWithHover = collapsedHoverActive
		? COLLAPSED_HOVER_WIDTH_PX
		: shellWidth;
	const buttonTransition = isMorphing
		? shellTransition
		: isMinimized
		  ? collapsedHoverTransition
		  : shellTransition;
	const chevronRevealTransition = isMorphing
		? chevronTransition
		: collapsedChevronTransition;

	return (
		<>
			<div
				ref={measureRef}
				className={cn(
					'pointer-events-none invisible absolute flex h-11 items-center whitespace-nowrap px-4',
					isWaiting
						? 'text-[14px] font-medium'
						: 'text-[18px] font-semibold tabular-nums',
				)}
				aria-hidden
			>
				<span className="size-[18px] shrink-0" />
				<span className="ml-2">{timeLabel}</span>
			</div>

			<button
				ref={buttonRef}
				type="button"
				role="timer"
				data-tutorial-demo="meeting-timer"
				aria-live="off"
				aria-label={
					isMinimized
						? `Expandir timer — ${ariaLabel}`
						: `Minimizar timer — ${ariaLabel}`
				}
				title={ariaLabel}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onClick={handleToggle}
				className={cn(
					'relative m-0 h-11 shrink-0 overflow-hidden border-0 bg-[#3c4043] p-0 leading-none text-[#e8eaed]',
					'cursor-pointer transition-colors hover:bg-[#45494d]',
					className,
				)}
				style={{
					width: shellWidthWithHover,
					marginLeft: shellMarginLeft,
					borderRadius: isMinimized
						? COLLAPSED_SIZE_PX / 2
						: EXPANDED_RADIUS_PX,
					transition: buttonTransition,
				}}
			>
				<span
					className="absolute inset-y-0 left-0 flex items-center overflow-hidden"
					style={{
						paddingLeft: COLLAPSED_CHEVRON_LEFT_PADDING_PX,
						width: collapsedHoverActive
							? COLLAPSED_CHEVRON_REVEAL_PX
							: 0,
						transition: chevronRevealTransition,
					}}
					aria-hidden
				>
					<ChevronLeft
						className="size-4 shrink-0 text-[#9aa0a6]"
						style={{
							opacity: collapsedHoverActive ? 1 : 0,
							transition: chevronRevealTransition,
						}}
						strokeWidth={2.5}
					/>
				</span>

				<div
					className="absolute top-1/2 -translate-y-1/2"
					style={{
						left: isMinimized
							? `calc(100% - ${COLLAPSED_CLOCK_OFFSET_PX}px)`
							: HORIZONTAL_PADDING_PX,
						transition: clockTransition,
					}}
				>
					<div className="relative flex size-[18px] items-center justify-center">
						<Clock
							className={cn(
								'size-[18px] text-[#9aa0a6] transition-opacity duration-150',
								!isMinimized && isHovered && 'opacity-0',
							)}
							strokeWidth={2.25}
						/>
						<Minus
							aria-hidden
							className={cn(
								'absolute inset-0 m-auto size-4 text-[#9aa0a6] transition-opacity duration-150',
								!isMinimized && isHovered
									? 'opacity-100'
									: 'opacity-0',
							)}
							strokeWidth={2.5}
						/>
					</div>
				</div>

				<span
					className={cn(
						'pointer-events-none absolute top-1/2 overflow-hidden whitespace-nowrap',
						isWaiting
							? 'text-[14px] font-medium tracking-normal'
							: 'text-[18px] font-semibold tabular-nums tracking-wide',
					)}
					style={{
						left: textLeftPx,
						maxWidth: isMinimized
							? 0
							: expandedWidth -
							  textLeftPx -
							  HORIZONTAL_PADDING_PX,
						opacity: isMinimized ? 0 : 1,
						transform: isMinimized
							? 'translateY(-50%) translateX(8px)'
							: 'translateY(-50%) translateX(0)',
						transition: textTransition,
					}}
				>
					{timeLabel}
				</span>
			</button>
		</>
	);
};

export { SessionCallMeetingTimer };
